import { OnInit, Component, inject, ChangeDetectionStrategy, effect, untracked } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

import { HomeService } from './home.service';
import { HomeModel } from './home.model';
import { AuthService } from '@services/core/auth.service';
import { AppSettings } from '@services/settings/app-settings.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { ListCardComponent } from 'app/core/components/list-card/list-card.component';

registerLocaleData(localeEs);

const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

@Component({
  selector: 'app-home',
  templateUrl: './home.html',
  styleUrls: ['./home.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    NgxChartsModule,
    NgbTooltipModule,
    PageHeaderComponent,
    ListCardComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [HomeService]
})
export class HomePage implements OnInit {
  private homeService = inject(HomeService);
  private authService = inject(AuthService);
  private router = inject(Router);
  public appSettings = inject(AppSettings);

  filter: HomeModel = new HomeModel();
  periods: any[] = [];
  loading = { kpis: true, charts: true, activity: true, ranking: true };
  loadError = false;
  private loadGen = 0;
  private periodReady = false;
  /** Evita pedir el dashboard antes de que decrypt-token deje al usuario. */
  private dashboardBootstrapped = false;
  readonly sparkWideDesktop: [number, number] = [240, 70];
  readonly sparkWideMobile: [number, number] = [140, 52];
  readonly sparkNarrowDesktop: [number, number] = [150, 50];
  readonly sparkNarrowMobile: [number, number] = [110, 44];
  combinedHistory: any[] = [];
  entityBars: any[] = [];

  stats: any = {
    totalSocios: 0,
    totalVehicles: 0,
    pendingRequests: 0,
    monthlyBilling: 0,
    totalBenefits: 0,
    periodName: '—'
  };
  charts: any = {
    entities: [],
    processes: [],
    billingHistory: [],
    benefitHistory: []
  };
  claims: any = { total: 0, open: 0, closed: 0, byStatus: [] };
  partiesWithoutClaims: any = { total: 0, items: [] };
  summaryByEntity: any[] = [];
  recentRequests: any[] = [];
  party: any = null;
  isPortalUser = false;
  roleCategory = '';
  private roleKey = '';

  colorScheme = { domain: ['#348FE2', '#00ACAC', '#727cb6', '#90CA4B', '#F59C1A', '#A8385D'] };
  colorLine = { domain: ['#348FE2'] };
  colorDual = { domain: ['#348FE2', '#ff5b57'] };
  colorBar = { domain: ['#348FE2'] };
  colorBilling = { domain: ['#348FE2'] };
  colorBenefit = { domain: ['#ff5b57'] };
  colorResult = { domain: ['#00ACAC'] };

  get isSocio(): boolean {
    return this.roleCategory === 'USER' || this.isPortalUser;
  }

  get isFinance(): boolean {
    return this.roleCategory === 'FINANZAS';
  }

  get isSecretary(): boolean {
    return this.roleCategory === 'SECRETARIA';
  }

  get isFullView(): boolean {
    return !this.isSocio && !this.isFinance && !this.isSecretary;
  }

  get hasCleanHistory(): boolean {
    return this.isSocio && Number(this.partiesWithoutClaims?.total || 0) > 0;
  }

  get billingSeries(): any[] {
    return this.charts?.billingHistory?.[0]?.series || [];
  }

  get benefitSeries(): any[] {
    return this.charts?.benefitHistory?.[0]?.series || [];
  }

  get netSeries(): any[] {
    const map = new Map<string, { bill: number; ben: number }>();
    for (const point of this.billingSeries) {
      const key = this._periodKey(point.name);
      map.set(key, { bill: Number(point.value || 0), ben: map.get(key)?.ben || 0 });
    }
    for (const point of this.benefitSeries) {
      const key = this._periodKey(point.name);
      const prev = map.get(key) || { bill: 0, ben: 0 };
      prev.ben = Number(point.value || 0);
      map.set(key, prev);
    }
    return [...map.keys()]
      .sort((a, b) => this._periodSort(a) - this._periodSort(b))
      .map((name) => ({ name, value: (map.get(name)?.bill || 0) - (map.get(name)?.ben || 0) }));
  }

  get billingSpark(): any[] {
    return this.toSpark('Recaudación', this.billingSeries);
  }

  get benefitSpark(): any[] {
    return this.toSpark('Beneficios', this.benefitSeries);
  }

  get resultSpark(): any[] {
    return this.toSpark('Resultado', this.netSeries);
  }

  get billingRows(): { name: string; value: number; percent: number }[] {
    return this.compareRows(this.billingSeries);
  }

  get benefitRows(): { name: string; value: number; percent: number }[] {
    return this.compareRows(this.benefitSeries);
  }

  get resultRows(): { name: string; value: number; percent: number }[] {
    return this.compareRows(this.netSeries);
  }

  get resultTotal(): number {
    return Number(this.stats.monthlyBilling || 0) - Number(this.stats.totalBenefits || 0);
  }

  get billingTrend(): number | null {
    return this.seriesTrend(this.billingSeries);
  }

  get benefitTrend(): number | null {
    return this.seriesTrend(this.benefitSeries);
  }

  get resultTrend(): number | null {
    return this.seriesTrend(this.netSeries);
  }

  get sparkWide(): [number, number] {
    return this.appSettings.isMobile() ? this.sparkWideMobile : this.sparkWideDesktop;
  }

  get sparkNarrow(): [number, number] {
    return this.appSettings.isMobile() ? this.sparkNarrowMobile : this.sparkNarrowDesktop;
  }

  yMin(series: { value?: number }[]): number {
    const values = (series || []).map((p) => Number(p.value || 0));
    if (!values.length) return 0;
    const min = Math.min(...values);
    return min >= 0 ? min * 0.92 : min * 1.08;
  }

  get claimRows(): { name: string; value: number; percent: number }[] {
    const total = Number(this.claims?.total || 0);
    return (this.claims?.byStatus || []).slice(0, 2).map((item: any) => {
      const value = Number(item.value || 0);
      return {
        name: item.name || item.statusSlug || 'Otros',
        value,
        percent: total ? (value / total) * 100 : 0
      };
    });
  }

  get heroCards(): any[] {
    if (this.isSecretary) {
      return [
        {
          title: 'Siniestros',
          hint: 'Siniestros del rango de periodos seleccionado, según tus entidades.',
          route: '/solicitudes',
          value: this.claims.total,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorBilling,
          trend: null,
          rows: this.claimRows,
          rowPrefix: '',
          rowShare: true,
          dotClass: 'text-warning',
          wide: true
        },
        {
          title: 'En proceso',
          hint: 'Solicitudes de siniestro que aún requieren seguimiento.',
          route: '/solicitudes',
          value: this.claims.open,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorBilling,
          trend: null,
          rows: [],
          subtitle: 'Requieren seguimiento',
          dotClass: 'text-info'
        },
        {
          title: 'Socios sin siniestros',
          hint: 'Socios con historial limpio en el rango seleccionado.',
          route: '/socios',
          value: this.partiesWithoutClaims.total,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorResult,
          trend: null,
          rows: [],
          subtitle: 'Historial limpio',
          dotClass: 'text-primary',
          needsRanking: true
        }
      ];
    }

    if (this.isSocio) {
      return [
        {
          title: 'Mis siniestros',
          hint: 'Tus siniestros en el rango de periodos seleccionado.',
          route: '/solicitudes',
          value: this.claims.total,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorBilling,
          trend: null,
          rows: this.claimRows,
          rowPrefix: '',
          rowShare: true,
          dotClass: 'text-warning',
          wide: true
        },
        {
          title: 'En proceso',
          hint: 'Solicitudes tuyas que aún están en curso.',
          route: '/solicitudes',
          value: this.claims.open,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorBilling,
          trend: null,
          rows: [],
          subtitle: 'Requieren seguimiento',
          dotClass: 'text-info'
        },
        {
          title: 'Vehículos',
          hint: 'Protecciones vigentes asociadas a tu ficha.',
          route: '/socios',
          action: 'party',
          value: this.stats.totalVehicles,
          money: false,
          spark: [],
          sparkSeries: [],
          color: this.colorResult,
          trend: null,
          rows: [],
          subtitle: 'Protecciones vigentes',
          dotClass: 'text-info'
        }
      ];
    }

    const lossSubtitle = this.lossRatio != null
      ? `${this.lossRatio.toFixed(0)}% de siniestralidad`
      : 'Recaudación menos beneficios pagados';

    const recaudacion = {
      title: 'Recaudación',
      hint: 'Primas cobradas según el rango de periodos seleccionado.',
      route: '/facturacion/primas',
      value: this.stats.monthlyBilling,
      money: true,
      spark: this.billingSpark,
      sparkSeries: this.billingSeries,
      color: this.colorBilling,
      trend: this.billingTrend,
      rows: this.billingRows,
      rowPrefix: 'Recaudación',
      dotClass: 'text-primary',
      wide: true
    };
    const beneficios = {
      title: 'Beneficios pagados',
      hint: 'Egresos pagados a socios en el rango de periodos seleccionado.',
      route: '/facturacion/reembolsos',
      value: this.stats.totalBenefits,
      money: true,
      spark: this.benefitSpark,
      sparkSeries: this.benefitSeries,
      color: this.colorBenefit,
      trend: this.benefitTrend,
      rows: this.benefitRows,
      rowPrefix: 'Beneficios',
      dotClass: 'text-danger'
    };

    if (this.isFinance) {
      return [
        recaudacion,
        beneficios,
        {
          title: 'Resultado',
          hint: 'Recaudación menos beneficios pagados en el rango seleccionado.',
          route: '/facturacion/primas',
          value: this.resultTotal,
          money: true,
          spark: this.resultSpark,
          sparkSeries: this.netSeries,
          color: this.colorResult,
          trend: this.resultTrend,
          rows: this.resultRows,
          rowPrefix: 'Resultado',
          subtitle: lossSubtitle,
          dotClass: 'text-teal'
        }
      ];
    }

    return [
      recaudacion,
      beneficios,
      {
        title: 'Solicitudes abiertas',
        hint: 'Solicitudes que requieren seguimiento en el rango seleccionado.',
        route: '/solicitudes',
        value: this.stats.pendingRequests,
        money: false,
        spark: [],
        sparkSeries: [],
        color: this.colorBilling,
        trend: null,
        rows: this.claimRows,
        rowPrefix: '',
        rowShare: true,
        subtitle: 'Requieren seguimiento',
        dotClass: 'text-warning'
      }
    ];
  }

  toSpark(name: string, series: any[]): any[] {
    return [{
      name,
      series: (series || []).map((point) => ({
        name: String(point?.name || ''),
        value: Number(point?.value || 0)
      }))
    }];
  }

  comparePercent(current: number, previous: number): number {
    const a = Number(current || 0);
    const b = Number(previous || 0);
    if (!b) return a ? 100 : 0;
    return ((a - b) / Math.abs(b)) * 100;
  }

  seriesTrend(series: any[]): number | null {
    if (!series || series.length < 2) return null;
    return this.comparePercent(series[series.length - 1]?.value, series[series.length - 2]?.value);
  }

  compareRows(series: any[], count = 2): { name: string; value: number; percent: number }[] {
    const points = [...(series || [])];
    if (!points.length) return [];
    const last = points.slice(-Math.min(count, points.length));
    return last.map((point, i) => {
      const idx = points.length - last.length + i;
      const prev = idx > 0 ? Number(points[idx - 1]?.value || 0) : 0;
      const value = Number(point?.value || 0);
      return { name: String(point?.name || ''), value, percent: this.comparePercent(value, prev) };
    }).reverse();
  }

  get lossRatio(): number | null {
    const billed = Number(this.stats.monthlyBilling || 0);
    if (!billed) return null;
    return (Number(this.stats.totalBenefits || 0) / billed) * 100;
  }

  get lossRatioClass(): string {
    const ratio = this.lossRatio;
    if (ratio == null) return 'text-muted';
    if (ratio >= 100) return 'text-danger';
    if (ratio >= 70) return 'text-warning';
    return 'text-success';
  }

  get entityTotal(): number {
    return (this.summaryByEntity || []).reduce((acc, item) => acc + Number(item.totalMonthlyContribution || 0), 0);
  }

  get claimStatusChart(): any[] {
    return (this.claims?.byStatus || []).map((item: any) => ({
      name: item.name || item.statusSlug || 'Otros',
      value: Number(item.value || 0)
    }));
  }

  entityShare(item: any): number {
    const total = this.entityTotal;
    if (!total) return 0;
    return Math.round((Number(item.totalMonthlyContribution || 0) / total) * 100);
  }

  trendUp(value: number | null): boolean {
    return value != null && value >= 0;
  }

  constructor() {
    // Home es ruta '' sin guard: monta antes de que decrypt-token deje al usuario.
    // La sesión va por cookie; el token de Microsoft ya no queda en el navegador.
    effect(() => {
      const user = this.authService.userData();
      const role = this.authService.sharingRole() as any;
      const key = `${role?.id || ''}|${String(role?.name || '').toUpperCase()}|${String(role?.category || '').toUpperCase()}`;
      untracked(() => {
        if (!user) return;

        this.isPortalUser = this.authService.isUserRole();
        this.roleCategory = String(role?.category || '').toUpperCase();

        if (!this.dashboardBootstrapped) {
          this.dashboardBootstrapped = true;
          this.roleKey = key;
          this.getPeriod();
          return;
        }

        if (key === this.roleKey) return;
        this.roleKey = key;
        this.fetchPanels();
      });
    });
  }

  ngOnInit(): void {
    this.isPortalUser = this.authService.isUserRole();
    this.roleCategory = String((this.authService.sharingRole() as any)?.category || '').toUpperCase();
  }

  get userName(): string {
    const user = this.authService.userData() as any;
    return user?.displayName || user?.mail || 'Bienvenido';
  }

  get roleLabel(): string {
    const role = this.authService.sharingRole() as any;
    return role?.displayName || role?.description || role?.name || '';
  }

  get periodOptions(): any[] {
    return (this.periods || []).map((p) => ({
      ...p,
      displayName: `${p.year} ${String(p.period).padStart(2, '0')} ${MONTHS[(p.period || 1) - 1] || ''}`
    }));
  }

  get endPeriodOptions(): any[] {
    const start = Number(this.filter.periodIdStart || 0);
    return this.periodOptions.filter((p) => Number(p.id) >= start);
  }

  getPeriod() {
    this.homeService.getPeriods().subscribe({
      next: (response) => {
        this.periods = response || [];
        this.defaultPeriods();
      },
      error: () => {
        this.periods = [];
        this.fetchPanels();
      }
    });
  }

  defaultPeriods() {
    if (!this.periods.length) {
      this.periodReady = true;
      this.fetchPanels();
      return;
    }
    const active = this.periods.find((p) => p.statusSlug === 'ACTIVE');
    const last = this.periods.reduce((prev, curr) => (curr.id > prev.id ? curr : prev));
    const end = active || last;
    const yearStart = this.periods.find((p) => p.year === end.year && Number(p.period) === 1);
    this.filter.periodIdStart = yearStart?.id || end.id;
    this.filter.periodIdEnd = end.id;
    this.periodReady = true;
    this.fetchPanels();
  }

  onPeriodChange() {
    if (!this.periodReady) return;
    const start = Number(this.filter.periodIdStart || 0);
    const end = Number(this.filter.periodIdEnd || 0);
    if (end < start) this.filter.periodIdEnd = this.filter.periodIdStart;
    this.fetchPanels();
  }

  fetchPanels() {
    const gen = ++this.loadGen;
    this.loading = { kpis: true, charts: true, activity: true, ranking: true };
    this.loadError = false;

    this.homeService.getSummary(this.filter).subscribe({
      next: (res) => {
        if (gen !== this.loadGen) return;
        this.stats = res?.stats || this.stats;
        this.charts = res?.charts || this.charts;
        this.claims = res?.claims || this.claims;
        this.party = res?.party || this.party;
        this.recentRequests = res?.recentRequests || [];
        if (Array.isArray(res?.summaryByEntity)) this.summaryByEntity = res.summaryByEntity;
        this.partiesWithoutClaims = res?.partiesWithoutClaims || this.partiesWithoutClaims;
        this.isPortalUser = this.authService.isUserRole();
        this.roleCategory = String(
          (this.authService.sharingRole() as any)?.category || res?.role?.category || ''
        ).toUpperCase();
        this.refreshChartViews();
        this.loading = { kpis: false, charts: false, activity: false, ranking: false };
      },
      error: () => {
        if (gen !== this.loadGen) return;
        this.loading = { kpis: false, charts: false, activity: false, ranking: false };
        this.loadError = true;
      }
    });
  }

  private refreshChartViews() {
    const billing = this.charts?.billingHistory?.[0]?.series || [];
    const benefits = this.charts?.benefitHistory?.[0]?.series || [];
    const map = new Map<string, { bill: number; ben: number }>();
    for (const point of billing) {
      const key = this._periodKey(point.name);
      map.set(key, { bill: Number(point.value || 0), ben: map.get(key)?.ben || 0 });
    }
    for (const point of benefits) {
      const key = this._periodKey(point.name);
      const prev = map.get(key) || { bill: 0, ben: 0 };
      prev.ben = Number(point.value || 0);
      map.set(key, prev);
    }
    const labels = [...map.keys()].sort((a, b) => this._periodSort(a) - this._periodSort(b));
    this.combinedHistory = [
      { name: 'Recaudación', series: labels.map((name) => ({ name, value: map.get(name)?.bill || 0 })) },
      { name: 'Beneficios', series: labels.map((name) => ({ name, value: map.get(name)?.ben || 0 })) }
    ];
    this.entityBars = (this.summaryByEntity || []).slice(0, 6).map((item) => ({
      name: item.campus || item.name || String(item.entityCode),
      value: Number(item.totalMonthlyContribution || 0)
    }));
  }

  go(path: string) {
    this.router.navigateByUrl(path);
  }

  openRequest(req: any) {
    if (req?.uuid) {
      this.router.navigate(['/solicitudes', req.uuid]);
      return;
    }
    this.router.navigateByUrl('/solicitudes');
  }

  openParty(uuid?: string) {
    const partyUuid = uuid || this.party?.uuid;
    if (partyUuid) {
      this.router.navigate(['/socios', partyUuid]);
      return;
    }
    this.router.navigateByUrl('/socios');
  }

  openHero(card: any) {
    if (card?.action === 'party') {
      this.openParty();
      return;
    }
    this.go(card?.route || '/');
  }

  hasSeries(chart: any[]): boolean {
    return Array.isArray(chart) && chart.some((s) => Array.isArray(s?.series) && s.series.length);
  }

  private _periodKey(name: string): string {
    const value = String(name || '');
    if (/^\d{4}-\d{2}$/.test(value)) {
      const [year, month] = value.split('-');
      return `${month}/${year}`;
    }
    return value;
  }

  private _periodSort(label: string): number {
    const [month, year] = String(label).split('/');
    return Number(year || 0) * 100 + Number(month || 0);
  }
}
